import { Component, signal } from '@angular/core';
import { AngularModule } from '../../../../modules/angular.module';
import { ListComponent } from '../list/list.component';
import { LocationService } from '../../../../services/location.service';
import { NavigationService } from '../../../../services/navigation.service';
import { AdminService } from '../../../../services/admin.service';
import { Location, LocationCreatePayload, LocationUpdatePayload } from '@mira-avant/mtlibs';
import { ZorroModule } from '../../../../modules/zorro.module';
import { LocationFormComponent } from '../../../forms/standard/location-form/location-form.component';
import { ClipboardService } from '../../../../services/clipboard.service';
import { Subscription } from 'rxjs';
import { SearchFormComponent } from '../../../forms/standard/search-form/search-form.component';

@Component({
	selector: 'app-location-list',
	standalone: true,
	imports: [AngularModule, ListComponent, ZorroModule, LocationFormComponent, SearchFormComponent],
	templateUrl: './location-list.component.html',
	styleUrl: './location-list.component.scss',
})
export class LocationListComponent {
	readonly locations = signal<Location[]>([]);
	readonly editLocation = signal<Location | undefined>(undefined);
	readonly loading = signal<boolean>(false);

	readonly LOCATIONS_PAGE_SIZE = 2;
	readonly totalLocations = signal<number>(0);
	readonly currentPage = signal<number>(1);

	private sub: Subscription | undefined;

	constructor(
		public locationService: LocationService,
		public nav: NavigationService,
		private adminService: AdminService,
		public clipboardService: ClipboardService
	) {}

	async ngOnInit(): Promise<void> {
		if (!this.adminService.selectedClient$.value) {
			throw new Error('No client selected');
		}

		this.sub = this.adminService.selectedClient$.subscribe(async client => {
			if (!client) {
				this.locations.set([]);
				this.totalLocations.set(0);
				return;
			}

			await this.refreshLocations();
		});
	}

	public async refreshLocations(): Promise<void> {
		if (!this.adminService.selectedClient$.value || !this.adminService.selectedClient$.value.uuid) {
			throw new Error('No client selected');
		}

		await this.loadPageOfLocations(1);

		await this.locationService
			.getTotalLocations(this.adminService.selectedClient$.value.uuid)
			.then((total: number) => {
				this.totalLocations.set(total);
			});
	}

	public async loadPageOfLocations(pageNumber: number): Promise<void> {
		if (!this.adminService.selectedClient$.value || !this.adminService.selectedClient$.value.uuid) {
			throw new Error('No client selected');
		}

		try {
			const locations = await this.locationService.getByClient$(
				this.adminService.selectedClient$.value.uuid,
				pageNumber - 1,
				this.LOCATIONS_PAGE_SIZE
			);

			this.locations.set(locations);
			this.currentPage.set(pageNumber);
		} catch (error) {
			console.error('Error loading locations:', error);
		}
	}

	public async onPageChange(pageNumber: number): Promise<void> {
		this.edit(undefined);
		await this.loadPageOfLocations(pageNumber);
	}

	public select(location: Location): void {
		if (!this.adminService.selectedClient$.value || !this.adminService.selectedClient$.value.uuid) {
			throw new Error('No client selected');
		}

		this.nav.navigate([
			'my',
			'admin',
			'lc',
			{
				clientUuid: this.adminService.selectedClient$.value.uuid,
				locationUuid: location.uuid,
			},
		]);
	}

	public edit(location?: Location): void {
		this.editLocation.set(this.editLocation()?.uuid === location?.uuid ? undefined : location);
	}

	public async delete$(location: Location): Promise<void> {
		this.loading.set(true);

		try {
			this.edit(undefined);

			await this.locationService.delete$(location.uuid);
			this.locations.set(this.locations().filter(c => c.uuid !== location.uuid));
			await this.refreshLocations();
		} catch (error) {
			console.error('Error deleting location:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public async moveUp$(location: Location): Promise<void> {
		if (!this.adminService.selectedClient$.value) {
			throw new Error('No client selected');
		}

		this.edit(undefined);
		this.loading.set(true);

		try {
			await this.locationService.moveUp$(location.uuid, this.adminService.selectedClient$.value.uuid);
			await this.loadPageOfLocations(this.currentPage());
		} catch (error) {
			console.error('Error moving location up:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public async moveDown$(location: Location): Promise<void> {
		if (!this.adminService.selectedClient$.value) {
			throw new Error('No client selected');
		}

		this.edit(undefined);
		this.loading.set(true);

		try {
			await this.locationService.moveDown$(location.uuid, this.adminService.selectedClient$.value.uuid);
			await this.loadPageOfLocations(this.currentPage());
		} catch (error) {
			console.error('Error moving location down:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public async onLocationFormSubmit$($event: any): Promise<void> {
		this.loading.set(true);

		try {
			if (this.editLocation()) {
				await this.updateLocation$($event);
			} else {
				await this.createNewLocation$($event);
			}
		} catch (error) {
			console.error('Error submitting location form:', error);
		} finally {
			this.loading.set(false);
		}
	}

	private async createNewLocation$($event: any): Promise<void> {
		if (!this.adminService.selectedClient$.value) {
			throw new Error('No client selected');
		}

		const createPayload: LocationCreatePayload = {
			client_uuid: this.adminService.selectedClient$.value.uuid,
			name: $event.name,
			active: true,
		};

		await this.locationService.create$(createPayload);
		await this.refreshLocations();
	}

	private async updateLocation$($event: any): Promise<void> {
		const currentEdit = this.editLocation();
		if (!currentEdit) {
			throw new Error('No location to update');
		}

		const updatePayload: LocationUpdatePayload = {
			name: $event.name,
		};

		await this.locationService.update$(currentEdit.uuid, updatePayload);

		// reload current page
		await this.loadPageOfLocations(this.currentPage());
	}

	public async onSearchFormSubmit$($event: any): Promise<void> {
		if (!this.adminService.selectedClient$.value) {
			throw new Error('No client selected');
		}

		if (!$event.search) {
			await this.refreshLocations();
			return;
		}

		this.loading.set(true);

		try {
			const locations = await this.locationService.searchLocations(
				this.adminService.selectedClient$.value.uuid,
				$event.search
			);

			this.locations.set(locations);
			this.currentPage.set(1);
			this.totalLocations.set(locations.length);
		} catch (error) {
			console.error('Error searching locations:', error);
		} finally {
			this.loading.set(false);
		}
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}
}
